import { createContext, useContext, useEffect, useState, useCallback, type ReactNode } from 'react';
import type { Session } from '@supabase/supabase-js';
import { supabase } from '../services/supabase';
import type { AuthSession, UserRole } from './types';
import { isLocalhost } from './authUtils';

interface AuthState {
  session: AuthSession | null;
  loading: boolean;
  bypassed: boolean;
}

interface AuthContextValue extends AuthState {
  login: (email: string, password: string, rememberMe: boolean) => Promise<{ error: string | null }>;
  logout: () => void;
  role: UserRole | null;
  isAdmin: boolean;
  isAtLeastAuthorized: boolean;
}

const AuthContext = createContext<AuthContextValue | null>(null);

export function AuthProvider({ children }: { children: ReactNode }) {
  const [state, setState] = useState<AuthState>({
    session: null,
    loading: true,
    bypassed: false,
  });

  const handleSupaSession = useCallback(async (supaSession: Session | null) => {
    if (!supaSession) {
      setState({ session: null, loading: false, bypassed: false });
      return;
    }

    if (!supabase) return;

    // authenticated kullanıcı kendi satırını RLS policy ile okur
    const { data, error } = await supabase
      .from('gmc_users')
      .select('id, email, role, display_name, is_active')
      .eq('email', supaSession.user.email!.toLowerCase())
      .single();

    if (error || !data || !data.is_active) {
       await supabase.auth.signOut();
       setState({ session: null, loading: false, bypassed: false });
       return;
    }

    setState({ 
      session: {
        token: supaSession.access_token,
        user_id: data.id,
        email: data.email,
        role: data.role as UserRole,
        display_name: data.display_name,
        expires_at: supaSession.expires_at ? supaSession.expires_at * 1000 : 0,
        remember_me: true
      }, 
      loading: false, 
      bypassed: false 
    });
  }, []);

  useEffect(() => {
    // Bypass sadece açıkça aktif edilmişse ve gerçekten localhost'taysa çalışır.
    // VITE_DISABLE_AUTH_BYPASS=true ile production build'lerinde devre dışı bırakılabilir.
    const bypassAllowed = isLocalhost() && import.meta.env.VITE_DISABLE_AUTH_BYPASS !== 'true';
    if (bypassAllowed) {
      // Localhost mock session — production'da asla etkin olmaz
      setState({
        session: {
          token: 'mock-token',
          user_id: 'mock-id',
          email: 'mock@localhost',
          role: 'admin',
          display_name: 'Local Admin',
          expires_at: Date.now() + 86400000,
          remember_me: true
        },
        loading: false,
        bypassed: true
      });
      return;
    }

    if (!supabase) {
      setState({ session: null, loading: false, bypassed: false });
      return;
    }

    // İlk yüklemede mevcut oturumu al
    supabase.auth.getSession().then(({ data: { session } }) => {
      handleSupaSession(session);
    });

    // Oturum değişikliklerini dinle
    const { data: authListener } = supabase.auth.onAuthStateChange(
      (_event, session) => {
        handleSupaSession(session);
      }
    );

    return () => {
      authListener.subscription.unsubscribe();
    };
  }, [handleSupaSession]);

  const login = useCallback(async (
    email: string,
    password: string,
    _rememberMe: boolean // Supabase GoTrue automatically handles session persistence via localStorage
  ): Promise<{ error: string | null }> => {
    if (!supabase) return { error: 'Supabase bağlantısı yok.' };

    const { error } = await supabase.auth.signInWithPassword({
      email: email.trim().toLowerCase(),
      password: password
    });

    if (error) {
      return { error: 'E-posta veya şifre hatalı.' };
    }

    return { error: null };
  }, []);

  const logout = useCallback(async () => {
    if (supabase) {
      await supabase.auth.signOut();
    }
    setState(s => ({ ...s, session: null }));
  }, []);

  const role = state.bypassed ? 'admin' : (state.session?.role ?? null);

  return (
    <AuthContext.Provider value={{
      ...state,
      login,
      logout,
      role,
      isAdmin: role === 'admin',
      isAtLeastAuthorized: role === 'admin' || role === 'authorized',
    }}>
      {children}
    </AuthContext.Provider>
  );
}

export function useAuth(): AuthContextValue {
  const ctx = useContext(AuthContext);
  if (!ctx) throw new Error('useAuth must be used within AuthProvider');
  return ctx;
}
